import React, { useState } from 'react';
import { Plus, Search, ChevronRight, RefreshCw, Loader2, Trash2, Pencil, Building2 } from 'lucide-react';
import { COLORS } from '../../lib/utils';
import { qboAPI, clientsAPI } from '../../lib/api';
import { toast } from 'sonner';
import EditClientModal from './EditClientModal';

const ENTITY_LABELS = {
  llc: 'LLC',
  smllc: 'SMLLC',
  multi_llc: 'Multi-Member LLC',
  s_corp: 'S-Corp',
  c_corp: 'C-Corp',
  sole_prop: 'Sole Proprietor',
  nonprofit: 'Not-for-Profit',
};

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // V19.10.6 — Format in Central Time so users see an explicit
  // timestamp ("Today, 2:14 PM CT" / "May 26, 2:14 PM CT") rather than
  // a relative "5h ago" that doesn't tell them when the data was last
  // pulled. The CPA workflow benefits from concrete timestamps.
  const tz = 'America/Chicago';
  const today = new Date();
  const sameDay = (
    d.toLocaleDateString('en-US', { timeZone: tz })
    === today.toLocaleDateString('en-US', { timeZone: tz })
  );
  const timePart = d.toLocaleTimeString('en-US', {
    timeZone: tz, hour: 'numeric', minute: '2-digit',
  });
  if (sameDay) return `Today, ${timePart} CT`;
  const datePart = d.toLocaleDateString('en-US', {
    timeZone: tz, month: 'short', day: 'numeric',
  });
  return `${datePart}, ${timePart} CT`;
}

export default function CompaniesLandingPage({ clients, onSelectClient, onAddClient, onRefreshClients }) {
  const [search, setSearch] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // client object
  const [editClient, setEditClient] = useState(null);       // client object

  const filteredClients = clients.filter(c =>
    !search ||
    (c.company || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleRefresh = async (client, e) => {
    e.stopPropagation();
    if (!client.qbo_connected) {
      toast.error('Connect this client to QuickBooks first.');
      return;
    }
    setRefreshingId(client.id);
    try {
      await qboAPI.getAccounts(client.id);
      toast.success(`Refreshed ${client.company} from QuickBooks`);
      if (onRefreshClients) await onRefreshClients();
    } catch (err) {
      console.error('QBO refresh failed:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Refresh failed: ${typeof detail === 'string' ? detail : 'Server error'}`);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleConfirmedDelete = async () => {
    if (!confirmDelete) return;
    const client = confirmDelete;
    try {
      await clientsAPI.remove(client.id);
      toast.success(`Deleted ${client.company}`);
      if (onRefreshClients) await onRefreshClients();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete client.');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="companies-landing-page" className="companies-landing" style={{ padding: '40px 48px', maxWidth: 1280, margin: '0 auto' }}>
      {/* Responsive overrides — V23 mobile pass. The grid columns collapse
          on phones into a stacked card. Header and toolbar wrap, the
          fixed-width client switcher in the app shell hides itself, and
          the search field becomes full-width. */}
      <style>{`
        @media (max-width: 900px) {
          .companies-landing { padding: 18px 14px !important; }
          .companies-landing h1 { font-size: 26px !important; }
          .companies-landing .companies-toolbar { width: 100%; flex-direction: column; align-items: stretch !important; gap: 10px !important; }
          .companies-landing .companies-search { width: 100% !important; }
          .companies-landing .companies-add-btn { width: 100%; justify-content: center; }
          .companies-landing .companies-header-row { display: none !important; }
          .companies-landing .companies-client-row {
            display: block !important;
            padding: 14px 14px !important;
          }
          .companies-landing .companies-client-row .company-cell {
            margin-bottom: 6px;
          }
          .companies-landing .companies-client-row .meta-row {
            display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 12px;
            color: ${COLORS.muted}; margin-top: 4px; margin-bottom: 10px;
          }
          .companies-landing .companies-client-row .meta-row > span > strong {
            color: ${COLORS.dark}; font-weight: 600;
          }
          .companies-landing .companies-client-row .actions-cell {
            justify-content: flex-end;
            border-top: 1px solid ${COLORS.border};
            padding-top: 10px;
          }
          .companies-landing .companies-client-row .actions-cell button {
            flex: 0 0 auto;
            white-space: nowrap !important;
          }
        }
        @media (max-width: 900px) {
          .edit-client-modal { max-width: 100% !important; max-height: 100vh !important; border-radius: 0 !important; margin: 0 !important; }
          .edit-client-contact-row { grid-template-columns: 1fr !important; gap: 6px !important; }
        }
      `}</style>

      {/* Show/hide rules for the meta-row and the three desktop-only
          columns. Desktop hides .mobile-only; mobile hides .desktop-cell. */}
      <style>{`
        .companies-landing .mobile-only { display: none; }
        @media (max-width: 900px) {
          .companies-landing .mobile-only { display: flex; }
          .companies-landing .desktop-cell { display: none !important; }
        }
      `}</style>

      <div className="companies-header" style={{ marginBottom: 32, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 className="font-heading" style={{ fontSize: 36, fontWeight: 500, color: COLORS.dark, letterSpacing: '-0.01em', marginBottom: 6 }}>
            Companies
          </h1>
          <p style={{ color: COLORS.muted, fontSize: 14 }}>
            {clients.length} {clients.length === 1 ? 'company' : 'companies'} — refresh QuickBooks data or open a dashboard.
          </p>
        </div>
        <div className="companies-toolbar" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="companies-search" style={{ position: 'relative', width: 280 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLORS.muted }} />
            <input
              data-testid="companies-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              style={{
                width: '100%',
                padding: '9px 12px 9px 34px',
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
                fontSize: 13,
                background: '#fff',
              }}
            />
          </div>
          <button
            data-testid="add-client-cta"
            className="companies-add-btn"
            onClick={onAddClient}
            style={{
              padding: '10px 18px',
              background: COLORS.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              whiteSpace: 'nowrap',
            }}
          >
            <Plus size={14} /> Add Client
          </button>
        </div>
      </div>

      <div style={{
        background: '#fff',
        borderRadius: 14,
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {/* Header row */}
        <div className="companies-header-row" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 200px 160px 180px 130px',
          gap: 16,
          padding: '12px 22px',
          background: '#FAFBFC',
          borderBottom: `1px solid ${COLORS.border}`,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: COLORS.muted,
          textTransform: 'uppercase',
        }}>
          <div>Company</div>
          <div>Entity</div>
          <div>Industry</div>
          <div>Last Sync</div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {filteredClients.length === 0 ? (
          <EmptyState onAdd={onAddClient} hasClients={clients.length > 0} />
        ) : (
          filteredClients.map((client, idx) => (
            <ClientRow
              key={client.id}
              client={client}
              isLast={idx === filteredClients.length - 1}
              refreshing={refreshingId === client.id}
              onRefresh={(e) => handleRefresh(client, e)}
              onEdit={(e) => { e.stopPropagation(); setEditClient(client); }}
              onDelete={(e) => { e.stopPropagation(); setConfirmDelete(client); }}
              onOpen={() => onSelectClient(client)}
            />
          ))
        )}
      </div>

      {confirmDelete && (
        <div
          onClick={() => setConfirmDelete(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,25,35,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
            style={{
              background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
              padding: 28, boxShadow: '0 24px 60px rgba(15,25,35,0.25)',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 12, background: '#FEE2E2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
            }}>
              <Trash2 size={22} color={COLORS.error} />
            </div>
            <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 600, color: COLORS.dark, marginBottom: 8 }}>
              Delete {confirmDelete.company}?
            </h3>
            <p style={{ fontSize: 14, color: COLORS.muted, marginBottom: 22, lineHeight: 1.55 }}>
              This permanently removes the company, its QuickBooks connection, all saved account mappings, and historical financial data. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                  background: '#fff', color: COLORS.dark, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={handleConfirmedDelete}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: COLORS.error, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Delete Company
              </button>
            </div>
          </div>
        </div>
      )}

      {editClient && (
        <EditClientModal
          client={editClient}
          token={localStorage.getItem('token')}
          onClose={() => setEditClient(null)}
          onSaved={() => { onRefreshClients && onRefreshClients(); }}
        />
      )}
    </div>
  );
}

function ClientRow({ client, isLast, refreshing, onRefresh, onEdit, onDelete, onOpen }) {
  const entityLabel = ENTITY_LABELS[client.entity_type] || '—';
  const isConnected = client.qbo_connected;
  const initials = (client.company || client.name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0])
    .join('')
    .toUpperCase();

  return (
    <div
      data-testid={`client-row-${client.id}`}
      className="companies-client-row"
      onClick={onOpen}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 200px 160px 180px 130px',
        gap: 16,
        padding: '14px 22px',
        alignItems: 'center',
        cursor: 'pointer',
        borderBottom: isLast ? 'none' : `1px solid ${COLORS.border}`,
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = '#F7FAFC'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div className="company-cell" style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: `linear-gradient(135deg, ${COLORS.primary}22, ${COLORS.primary}44)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: COLORS.primary,
          flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="font-heading" style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {client.company || client.name}
          </div>
          <div style={{ fontSize: 12, color: COLORS.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {client.name && client.company ? client.name : (client.contact_email || '—')}
          </div>
        </div>
      </div>

      <div className="desktop-cell" style={{ fontSize: 13, color: COLORS.dark }}>{entityLabel}</div>

      <div className="desktop-cell" style={{ fontSize: 13, color: client.industry ? COLORS.dark : COLORS.muted }}>
        {client.industry || '—'}
      </div>

      <div className="desktop-cell" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: isConnected ? COLORS.success : COLORS.muted,
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 13, color: COLORS.dark }}>
          {isConnected ? relativeTime(client.last_sync) : 'Not connected'}
        </span>
      </div>

      {/* Mobile-only meta row. Surfaces the three columns that get
          hidden on phones. */}
      <div className="meta-row mobile-only">
        <span><strong>Entity:</strong> {entityLabel || '—'}</span>
        <span><strong>Industry:</strong> {client.industry || '—'}</span>
        <span>
          <strong>Last Sync:</strong> {isConnected ? relativeTime(client.last_sync) : 'Not connected'}
        </span>
      </div>

      <div className="actions-cell" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        <button
          data-testid={`refresh-${client.id}`}
          onClick={onRefresh}
          disabled={refreshing || !isConnected}
          title={isConnected ? 'Refresh from QuickBooks' : 'Connect QuickBooks first'}
          style={{
            padding: '7px 10px',
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: refreshing ? COLORS.subtle : '#fff',
            color: isConnected ? COLORS.dark : COLORS.muted,
            fontSize: 11,
            fontWeight: 600,
            cursor: isConnected && !refreshing ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: isConnected ? 1 : 0.5,
          }}
        >
          {refreshing
            ? <><Loader2 size={12} className="animate-spin" /> Syncing…</>
            : <><RefreshCw size={12} /> Refresh</>}
        </button>
        <button
          data-testid={`edit-${client.id}`}
          onClick={onEdit}
          title="Edit name & contacts"
          style={{
            padding: '7px 9px',
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: '#fff',
            color: COLORS.muted,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ECFDF5';
            e.currentTarget.style.color = COLORS.primary;
            e.currentTarget.style.borderColor = '#A7F3D0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.color = COLORS.muted;
            e.currentTarget.style.borderColor = COLORS.border;
          }}
        >
          <Pencil size={12} />
        </button>
        <button
          data-testid={`delete-${client.id}`}
          onClick={onDelete}
          title="Delete company"
          style={{
            padding: '7px 9px',
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: '#fff',
            color: COLORS.muted,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#FEF2F2';
            e.currentTarget.style.color = COLORS.error;
            e.currentTarget.style.borderColor = '#FECACA';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.color = COLORS.muted;
            e.currentTarget.style.borderColor = COLORS.border;
          }}
        >
          <Trash2 size={12} />
        </button>
        <ChevronRight size={16} color={COLORS.muted} />
      </div>
    </div>
  );
}

function EmptyState({ onAdd, hasClients }) {
  return (
    <div style={{
      padding: '60px 24px',
      textAlign: 'center',
    }}>
      <div style={{
        width: 56,
        height: 56,
        borderRadius: 14,
        background: COLORS.subtle,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 14,
      }}>
        <Building2 size={24} color={COLORS.muted} />
      </div>
      <div className="font-heading" style={{ fontSize: 16, fontWeight: 600, color: COLORS.dark, marginBottom: 4 }}>
        {hasClients ? 'No companies match your search' : 'No companies yet'}
      </div>
      <p style={{ color: COLORS.muted, fontSize: 13, maxWidth: 380, margin: '0 auto 16px' }}>
        {hasClients ? 'Try a different search term.' : 'Add your first client to start generating financial reports.'}
      </p>
      {!hasClients && (
        <button
          onClick={onAdd}
          style={{
            padding: '9px 18px',
            background: COLORS.primary,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Plus size={14} /> Add Your First Client
        </button>
      )}
    </div>
  );
}
